import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';
import {
  MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon,
  FunnelIcon, EyeIcon,
} from '@heroicons/react/24/outline';
import styles from './ActivityPage.module.css';

const STATUSES = ['', 'pending', 'failed', 'cancelled'];
const STATUS_LABELS = {
  '':          'All',
  pending:     'Never finished',
  failed:      'Declined',
  cancelled:   'Cancelled',
};

// Plain words for what happened, because "pending" means something different
// to a cashier than it does to a payment gateway.
const STATUS_HELP = {
  pending:   'Reached the payment page and nothing came back',
  failed:    'The gateway declined it',
  cancelled: 'They backed out of the payment',
};

const STATUS_BADGE = {
  pending:   'badge-warning',
  failed:    'badge-inactive',
  cancelled: 'badge-inactive',
};

const PROVIDER_LABELS = {
  cybersource: 'Card',
  whish:       'Whish',
  store:       'Pay at store',
};

const DAY_RANGES = [
  { value: '',   label: 'Any time' },
  { value: '7',  label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

export default function AbandonedPage() {
  const navigate = useNavigate();
  const [rows,       setRows]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search,     setSearch]     = useState('');
  const [status,     setStatus]     = useState('');
  const [days,       setDays]       = useState('');
  const [loading,    setLoading]    = useState(true);
  const debounceRef = useRef(null);

  const load = useCallback((p, s, st, d) => {
    setLoading(true);
    adminAPI.getAbandoned({
      page: p, limit: 20,
      search: s || undefined,
      status: st || undefined,
      days:   d || undefined,
    })
      .then(res => {
        setRows(res.payments || []);
        setTotal(res.total || 0);
        setTotalPages(Math.max(1, Math.ceil((res.total || 0) / (res.limit || 20))));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Read through a ref so that changing a filter uses whatever is in the
  // search box without the effect refiring on every keystroke — that is the
  // debounce's job, and listing search here would undo it.
  const searchRef = useRef('');
  searchRef.current = search;
  useEffect(() => { setPage(1); load(1, searchRef.current, status, days); }, [status, days, load]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); load(1, val, status, days); }, 320);
  };

  const handlePage = (p) => { setPage(p); load(p, search, status, days); };

  const pages = [];
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, page + 2);
  for (let i = start; i <= end; i++) pages.push(i);

  // Somebody who abandoned a card payment and then paid a cashier is not a
  // lost sale. Counting them would overstate the problem and chasing them
  // would be a mistake, so they are marked and counted separately.
  const stillNotVip = rows.filter(r => !r.isNowVip).length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Unfinished checkouts</h1>
          <p>
            {total.toLocaleString()} payment{total === 1 ? '' : 's'} that never completed
            {rows.length > 0 && ` · ${stillNotVip} of those on this page are still not members`}
          </p>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <MagnifyingGlassIcon style={{ width:15, height:15, color:'var(--text-muted)' }} />
          <input
            className={styles.searchInput}
            placeholder="Search by name, phone, or email…"
            value={search}
            onChange={e => handleSearch(e.target.value)}
          />
        </div>
        <div className={styles.filterRight}>
          <FunnelIcon style={{ width:14, height:14, color:'var(--text-muted)' }} />
          {STATUSES.map(s => (
            <button
              key={s}
              className={`${styles.statusTab} ${status === s ? styles.statusTabActive : ''}`}
              onClick={() => setStatus(s)}
              title={STATUS_HELP[s] || 'Everything that did not complete'}
            >
              {STATUS_LABELS[s]}
            </button>
          ))}
          <select
            className={styles.statusTab}
            value={days}
            onChange={e => setDays(e.target.value)}
            aria-label="Time range"
            style={{ cursor:'pointer' }}
          >
            {DAY_RANGES.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div className="table-wrap" style={{ border:'none', borderRadius:0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Phone</th>
                <th>Method</th>
                <th>Amount</th>
                <th>What happened</th>
                <th>Member now?</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    {[140, 160, 110, 80, 80, 120, 90, 40].map((w, j) => (
                      <td key={j}><div className="skeleton" style={{ height:16, width:w, borderRadius:6 }} /></td>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr><td colSpan={8}>
                  <div className="empty-state">
                    <p>No unfinished checkouts for this filter.</p>
                  </div>
                </td></tr>
              ) : rows.map(r => (
                <tr key={r.id}>
                  <td style={{ whiteSpace:'nowrap' }}>
                    {r.createdAt ? format(new Date(r.createdAt), 'd MMM yyyy HH:mm') : '—'}
                  </td>
                  <td>
                    <div style={{ fontWeight:600 }}>{r.user.name || '—'}</div>
                    {r.user.email && (
                      <div style={{ fontSize:12, color:'var(--text-muted)' }}>{r.user.email}</div>
                    )}
                  </td>
                  <td style={{ whiteSpace:'nowrap' }}>{r.user.mobile || '—'}</td>
                  <td>{PROVIDER_LABELS[r.provider] || r.provider}</td>
                  <td style={{ whiteSpace:'nowrap' }}>
                    {r.currency === 'USD' ? '$' : ''}{r.amount.toFixed(2)}
                  </td>
                  <td>
                    <span className={`badge ${STATUS_BADGE[r.status] || 'badge-inactive'}`}>
                      {STATUS_LABELS[r.status] || r.status}
                    </span>
                    {r.failureReason && (
                      <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>
                        {r.failureReason}
                      </div>
                    )}
                  </td>
                  <td>
                    {r.isNowVip
                      ? <span className="badge badge-active">Joined since</span>
                      : <span style={{ color:'var(--text-muted)' }}>Not a member</span>}
                  </td>
                  <td>
                    <button
                      className="btn-icon"
                      title="Open this member"
                      onClick={() => navigate(`/members/${r.user.id}`)}
                    >
                      <EyeIcon style={{ width:16, height:16 }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button className={styles.pageBtn} disabled={page === 1} onClick={() => handlePage(page - 1)}>
              <ChevronLeftIcon style={{ width:14, height:14 }} />
            </button>
            {pages.map(p => (
              <button
                key={p}
                className={`${styles.pageBtn} ${p === page ? styles.pageBtnActive : ''}`}
                onClick={() => handlePage(p)}
              >
                {p}
              </button>
            ))}
            <button className={styles.pageBtn} disabled={page === totalPages} onClick={() => handlePage(page + 1)}>
              <ChevronRightIcon style={{ width:14, height:14 }} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
